"use client";

import BottomCta from "@/components/BottomCta";
import Link from "next/link";
import { motion } from "framer-motion";
import { Wallet, Globe, ShieldCheck, Briefcase, User, Building2, MessageSquareMore, FileCheck, Send, BadgeCheck, CheckCircle2 } from "lucide-react";

export default function BankAccountOpeningPage() {
  return (
    <main className="bg-[#FAFAFA] min-h-screen w-full overflow-x-hidden text-[#000000]">

      {/* ── HERO SECTION ── */}
      <section className="relative min-h-[85vh] flex items-center justify-center py-20 lg:py-32 2xl:py-48 overflow-hidden">
        <div className="max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto w-full px-6 text-center flex flex-col items-center relative z-10">
          
          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#B8893A]/20 bg-[#B8893A]/5 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-[#B8893A]" />
            <span className="text-[10px] sm:text-xs font-bold text-[#B8893A] uppercase tracking-[0.2em]">
              UAE Bank Account
            </span>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="text-4xl sm:text-5xl md:text-6xl xl:text-7xl 2xl:text-8xl font-bold tracking-tight leading-[1.15] mb-6 max-w-5xl text-[#000000]"
          >
            Why You Need a <br />
            <span className="font-serif font-normal text-[#B8893A]">UAE Bank Account?</span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="text-gray-500 text-base sm:text-lg md:text-xl xl:text-2xl max-w-2xl xl:max-w-4xl font-light leading-relaxed mb-10"
          >
            A UAE bank account gives you the financial foundation to operate smoothly and grow with confidence.
          </motion.p>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
            <Link href="/contact" className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-white bg-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#a3762f] hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide">
              Open Your Account
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section className="px-4 sm:px-8 lg:px-12 py-10 xl:py-22 bg-[#FAFAFA]">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Account Benefits</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Secure UAE banking solutions</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 xl:gap-5 ">
            {[
              { icon: Wallet, title: "Manage personal and business finances easily", text: "Keep your personal and company finances organized with secure UAE banking solutions." },
              { icon: Globe, title: "Receive and send international payments", text: "Transfer funds globally with reliable banking networks and multi-currency support." },
              { icon: ShieldCheck, title: "Access secure and reliable banking services", text: "Benefit from trusted banking facilities designed for individuals and businesses." },
              { icon: Briefcase, title: "Support your business setup and operations", text: "Get banking solutions that help manage day-to-day operations and business growth." },
            ].map((item, i) => {
              const IconComponent = item.icon;
              return (
                <div key={i} className="group bg-white p-8 rounded-2xl border border-gray-100/80 transition-all duration-300 shadow-md  hover:shadow-xl flex flex-col justify-between">
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-[#B8893A]/5 flex items-center justify-center text-[#B8893A] mb-6 group-hover:bg-[#B8893A] group-hover:text-white transition-all duration-300">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-[#B8893A] transition-colors duration-300">{item.title}</h3>
                    <p className="text-gray-500 font-light text-sm leading-relaxed">{item.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── WHAT WE DO & ACCOUNT TYPES ── */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-white border-y border-gray-100">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="text-xs font-bold text-[#B8893A] uppercase tracking-[0.2em] mb-4">What We Do</div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">End-to-end banking support<br /><span className="font-serif font-normal text-[#B8893A]">from start to finish.</span></h2>
            <div className="w-12 h-[3px] bg-[#B8893A] mb-6 rounded-full" />
            <div className="flex flex-col gap-3">
              {[
                "Bank selection based on your profile",
                "Document preparation and review",
                "Application submission and follow-up",
                "Coordination with bank representatives",
                "Ongoing guidance until account activation",
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-[#FAFAFA] border border-gray-100 shadow-sm rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-[#B8893A] shrink-0" />
                  <span className="text-sm font-semibold text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold text-[#B8893A] uppercase tracking-[0.2em] mb-4">Account Types</div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">Accounts for<br /><span className="font-serif font-normal text-[#B8893A]">Every Need</span></h2>
            <div className="w-12 h-[3px] bg-[#B8893A] mb-6 rounded-full" />
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: "Personal Bank Accounts", icon: User },
                { title: "Corporate Bank Accounts", icon: Building2 },
                { title: "Business Startup Accounts", icon: Briefcase },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex flex-col items-center justify-center shadow-md p-6 bg-[#FAFAFA] border border-gray-100 rounded-xl text-center">
                    <Icon className="w-8 h-8 text-[#B8893A] mb-3" />
                    <span className="text-sm font-bold text-gray-700">{item.title}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── OUR PROCESS ── */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32 bg-[#FAFAFA]">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Our Process</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Beyond setup, we provide the infrastructure for your success in the Emirates.</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
            {[
              { num: "01", icon: MessageSquareMore, title: "Consultation", text: "We understand your needs and recommend the right bank." },
              { num: "02", icon: FileCheck, title: "Documentation", text: "We guide you in preparing accurate and complete documents." },
              { num: "03", icon: Send, title: "Application", text: "We handle submission and coordinate directly with the bank." },
              { num: "04", icon: BadgeCheck, title: "Approval & Activation", text: "We follow up until your account is fully active and ready to use." },
            ].map((step, i) => {
              const IconComponent = step.icon;
              return (
                <div key={i} className="relative group p-8 rounded-2xl bg-white border border-gray-100 shadow-sm">
                  <div className="absolute top-4 right-6 text-5xl font-black text-[#B8893A]/10 group-hover:text-[#B8893A]/20 transition-colors">{step.num}</div>
                  <div className="w-14 h-14 rounded-xl bg-white border border-[#EAEAEA] flex items-center justify-center mb-6">
                    <IconComponent size={24} className="text-[#B8893A]" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 tracking-tight text-[#0A0A0A]">{step.title}</h3>
                  <p className="text-gray-500 font-light text-sm leading-relaxed">{step.text}</p>
                </div>
              );
            })}
          </div>

          {/* DOCUMENTS NEEDED */}
          <div className="mt-16 bg-white border border-[#EAEAEA] rounded-3xl p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-72 h-72 bg-[#B8893A]/5 rounded-full blur-3xl" />
            <div className="max-w-3xl mb-10 relative z-10">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#B8893A]/10 text-[#B8893A] text-sm font-semibold mb-4">Required Documents</span>
              <h3 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A] mb-4">
                Documents Needed For <span className="font-serif font-normal text-[#B8893A]">Bank Account Opening</span>
              </h3>
              <p className="text-gray-600">Preparing these documents in advance helps accelerate approval timelines and ensures a smooth onboarding experience with your selected banking partner.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 relative z-10">
              {[
                "Valid Passport Copy", "Visa or Entry Status", "Emirates ID", "Proof of Address", "Business Documents"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4 bg-[#FAFAFA] border border-[#ECECEC] rounded-2xl p-5">
                  <div className="w-12 h-12 rounded-xl bg-[#B8893A]/10 flex items-center justify-center shrink-0">
                    <CheckCircle2 size={22} className="text-[#B8893A]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#0A0A0A]">{item}</h4>
                    <p className="text-xs text-gray-500 mt-1">Required for verification.</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-white border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Why Choose Us</span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">Local bank introductions with professional account setup</h2>
            <p className="text-gray-500 font-light leading-relaxed">We help your company access UAE banking without unnecessary delays, using the right bank profile and local documentation strategy.</p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              { title: "Clear and Honest Guidance", text: "Receive transparent advice on banking requirements, compliance expectations, and account eligibility before you apply." },
              { title: "Fast and Organized Process", text: "We prepare and review your documentation to ensure it meets bank requirements and reduces unnecessary delays." },
              { title: "Complete Banking Support", text: "From application submission to bank communication, we support you throughout the entire account opening process." },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-start gap-3 p-6 border border-[#F2EAD8] bg-[#FAFAFA] rounded-xl shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-[#FDF5E6] flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-[#B8893A]" />
                </div>
                <span className="text-sm font-bold text-[#1A1A1A]">{item.title}</span>
                <span className="text-xs text-[#5B5B5B] leading-relaxed">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BottomCta
         image="/bcta.webp"
        title="Your account made easy"
        subtitle="Contact us today and take the first step towards secure and hassle-free banking."
      />
    </main>
  );
}
